import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import { semantic, typography } from '~/theme';

interface ReportGenerationProgressProps {
  loading: boolean;
  elapsedSeconds?: number;
  isRegenerate?: boolean;
}

interface StepConfig {
  key: string;
  label: string;
  icon: React.ReactElement;
}

const STEPS: StepConfig[] = [
  {
    key: 'prepare',
    label: 'Preparing report request',
    icon: <PersonOutlineIcon sx={{ fontSize: 16 }} />,
  },
  {
    key: 'context',
    label: 'Building clinical context',
    icon: <HistoryIcon sx={{ fontSize: 16 }} />,
  },
  {
    key: 'generate',
    label: 'Generating report',
    icon: <DescriptionIcon sx={{ fontSize: 16 }} />,
  },
  {
    key: 'finalize',
    label: 'Finalizing report',
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  },
];

const REGEN_STEPS: StepConfig[] = [
  {
    key: 'prepare',
    label: 'Preparing regeneration request',
    icon: <PersonOutlineIcon sx={{ fontSize: 16 }} />,
  },
  {
    key: 'context',
    label: 'Rebuilding clinical context',
    icon: <HistoryIcon sx={{ fontSize: 16 }} />,
  },
  {
    key: 'generate',
    label: 'Regenerating report',
    icon: <DescriptionIcon sx={{ fontSize: 16 }} />,
  },
  {
    key: 'finalize',
    label: 'Finalizing report',
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  },
];

// Seconds to spend on each step before advancing to the next.
const STEP_DURATIONS = [2, 3, 6, 4];

const ReportGenerationProgress: React.FC<ReportGenerationProgressProps> = ({
  loading,
  elapsedSeconds = 0,
  isRegenerate = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = isRegenerate ? REGEN_STEPS : STEPS;

  useEffect(() => {
    if (!loading) {
      setActiveStep(0);
      return;
    }

    let cumulative = 0;
    let nextStep = 0;
    for (let i = 0; i < steps.length; i += 1) {
      cumulative += STEP_DURATIONS[i];
      if (elapsedSeconds < cumulative) {
        nextStep = i;
        break;
      }
      nextStep = i + 1;
    }
    nextStep = Math.min(nextStep, steps.length - 1);
    setActiveStep(nextStep);
  }, [loading, elapsedSeconds, steps]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={0.5}
          >
            {isRegenerate ? 'Report Regeneration' : 'Report Generation'}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {elapsedSeconds > 0 ? `${elapsedSeconds}s` : 'Starting...'}
          </Typography>
        </Box>

        <Box sx={{ position: 'relative', pl: 0.5 }}>
          {steps.map((step, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            const isPending = index > activeStep;

            return (
              <Box
                key={step.key}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  position: 'relative',
                  pb: index === steps.length - 1 ? 0 : 1.5,
                  transition: 'all 0.35s ease',
                  opacity: isPending ? 0.5 : 1,
                }}
              >
                {/* Connector line */}
                {index !== steps.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 11,
                      top: 24,
                      width: 2,
                      height: 'calc(100% - 24px)',
                      bgcolor: isCompleted ? semantic.success.main : 'divider',
                      transition: 'background-color 0.4s ease',
                      borderRadius: 1,
                    }}
                  />
                )}

                {/* Step icon badge */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    bgcolor: isCompleted
                      ? semantic.success.bg
                      : isActive
                        ? `${semantic.primary.main}14`
                        : 'background.paper',
                    color: isCompleted
                      ? semantic.success.main
                      : isActive
                        ? semantic.primary.main
                        : 'text.disabled',
                    border: '2px solid',
                    borderColor: isCompleted
                      ? semantic.success.main
                      : isActive
                        ? semantic.primary.main
                        : 'divider',
                    transition: 'all 0.35s ease',
                    ...(isActive && {
                      animation: 'reportStepPulse 2s ease-in-out infinite',
                      '@keyframes reportStepPulse': {
                        '0%, 100%': {
                          boxShadow: `0 0 0 0 ${semantic.primary.main}44`,
                        },
                        '50%': {
                          boxShadow: `0 0 0 6px ${semantic.primary.main}00`,
                        },
                      },
                    }),
                  }}
                >
                  {isCompleted ? (
                    <CheckIcon sx={{ fontSize: 14 }} />
                  ) : (
                    step.icon
                  )}
                </Box>

                {/* Step label and status */}
                <Box sx={{ pt: 0.25, flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: typography.caption.fontSize,
                      fontWeight: isActive ? 700 : isCompleted ? 600 : 500,
                      color: isActive ? 'text.primary' : isPending ? 'text.disabled' : 'text.secondary',
                      transition: 'all 0.35s ease',
                    }}
                  >
                    {step.label}
                  </Typography>
                  {isActive && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.25,
                        '&::after': {
                          content: '""',
                          display: 'inline-block',
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          animation: 'reportDotPulse 1.4s infinite both',
                          '@keyframes reportDotPulse': {
                            '0%, 100%': { opacity: 0.3, transform: 'scale(0.6)' },
                            '50%': { opacity: 1, transform: 'scale(1)' },
                          },
                        },
                      }}
                    >
                      In progress
                    </Typography>
                  )}
                  {isCompleted && (
                    <Typography variant="caption" color="success.main" sx={{ fontSize: '0.65rem', fontWeight: 600, display: 'block', mt: 0.25 }}>
                      Complete
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
  );
};

export default ReportGenerationProgress;
